/**
 * BugDetailPage - Task/bug detail view with pull requests
 *
 * Features:
 * - Task information and status
 * - Agent activity timeline (thinking, tool calls)
 * - Pull requests list
 * - Link to Jira ticket
 */

import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, GitPullRequest, Bug, Calendar, Clock, CheckCircle2, Timer } from 'lucide-react';
import { tasksApi } from '../lib/tasks-api';
import { ticketPath } from '../lib/tickets-api';
import type { Task, PullRequest, TaskPatch, TaskUsageSummary, NoPrReviewResult } from '../lib/tasks-api';
import { parseAgentMessages } from '../lib/agent-message-parser';
import type { AgentStep } from '../lib/agent-message-parser';
import { StatusBadge, LoadingState, ErrorMessage, Card } from '../components/common';
import { AgentTimeline, UsageStats, PatchList, NoPrDecisionCard, RunTicketHeading } from '../components/task';
import { formatDuration } from '../lib/date-utils';
import { useAuth } from '../contexts/AuthContext';

export function BugDetailPage() {
  const { projectId, taskId } = useParams<{ projectId: string; taskId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [task, setTask] = useState<Task | null>(null);
  const [pullRequests, setPullRequests] = useState<PullRequest[]>([]);
  const [patches, setPatches] = useState<TaskPatch[]>([]);
  const [agentSteps, setAgentSteps] = useState<AgentStep[]>([]);
  const [usage, setUsage] = useState<TaskUsageSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTaskDetail = useCallback(async () => {
    if (!taskId) return;

    try {
      setIsLoading(true);
      const [taskData, prsData, messagesData, usageData, patchesData] = await Promise.all([
        tasksApi.getTask(taskId),
        tasksApi.getPullRequests(taskId),
        tasksApi.getMessages(taskId).catch(() => ({ messages: null })),
        isAdmin ? tasksApi.getUsage(taskId).catch(() => ({ usage: null })) : Promise.resolve({ usage: null }),
        tasksApi.getPatches(taskId).catch(() => ({ patches: [] })),
      ]);
      setTask(taskData.task);
      setPullRequests(prsData.pullRequests);
      const timeline = parseAgentMessages(messagesData.messages);
      setAgentSteps(timeline.steps);
      setUsage(usageData.usage);
      setPatches(patchesData.patches);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch task detail:', err);
      setError('Failed to load task');
    } finally {
      setIsLoading(false);
    }
  }, [taskId, isAdmin]);

  useEffect(() => {
    if (!taskId) return;
    fetchTaskDetail();
  }, [taskId, fetchTaskDetail]);

  const handleNoPrReviewed = useCallback((result: NoPrReviewResult) => {
    setTask((prev) => (prev ? { ...prev, ...result } : prev));
  }, []);

  const hasNoPrDecision = Boolean(task?.noPrReviewStatus);

  if ((error || !task) && !isLoading) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate(projectId ? `/projects/${projectId}` : '/projects')}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Project
        </button>
        <ErrorMessage message={error || 'Task not found'} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <LoadingState message="Loading task..." isLoading={isLoading} />
      {/* Header */}
      <div>
        <button
          onClick={() => navigate(`/projects/${task?.project.id}`)}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to {task?.project.name}
        </button>
        <Card>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              {/* Gradient icon container */}
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/25">
                <Bug className="w-6 h-6 text-white" />
              </div>
              <div>
                {task && (
                  <RunTicketHeading
                    ticketKey={task.ticketKey}
                    ticketUrl={task.ticketUrl}
                    prUrls={pullRequests.map((pr) => pr.prUrl)}
                    totalCostCents={usage?.totalCostCents}
                  />
                )}
                <p className="text-gray-500 dark:text-gray-400">
                  {task?.ticketTitle || 'No title'}
                </p>
                {task && (
                  <Link
                    to={ticketPath(task.project.id, task.ticketKey)}
                    className="mt-1 mr-4 inline-block text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
                  >
                    All runs of {task.ticketKey}
                  </Link>
                )}
                {task?.resumedFromTaskId && (
                  <Link
                    to={`/projects/${task.project.id}/bugs/${task.resumedFromTaskId}`}
                    className="mt-1 inline-block text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
                  >
                    Continues the run that asked for a product decision
                  </Link>
                )}
              </div>
            </div>
            {task?.status && <StatusBadge status={task.status} size="lg" />}
          </div>

          {/* Footer metadata */}
          <div className="flex items-center flex-wrap gap-x-6 gap-y-2 mt-4 pt-4 border-t border-gray-200/60 dark:border-gray-700/50">
            <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
              <Calendar className="w-4 h-4" />
              {task?.createdAt && <span>Created {new Date(task.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>}
            </div>
            {task?.startedAt && (
              <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
                <Clock className="w-4 h-4" />
                <span>Started {new Date(task?.startedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
              </div>
            )}
            {task?.completedAt && (
              <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
                <CheckCircle2 className="w-4 h-4" />
                <span>Completed {new Date(task.completedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
              </div>
            )}
            {task?.startedAt && task?.completedAt && (
              <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
                <Timer className="w-4 h-4" />
                <span>Duration {formatDuration(new Date(task?.completedAt).getTime() - new Date(task?.startedAt).getTime())}</span>
              </div>
            )}
            <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
              <GitPullRequest className="w-4 h-4" />
              <span>{pullRequests.length} {pullRequests.length === 1 ? 'PR' : 'PRs'}</span>
            </div>
          </div>
        </Card>
      </div>

      {/* Error Message */}
      {task?.errorMessage && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
          <p className="text-sm font-medium text-red-800 dark:text-red-200">Error</p>
          <p className="text-sm text-red-600 dark:text-red-300 mt-1">{task?.errorMessage}</p>
        </div>
      )}

      {/* Fallback Patches (publish failed after the fix was committed) */}
      {taskId && <PatchList taskId={taskId} patches={patches} />}

      {/* Agent Activity */}
      {agentSteps.length > 0 && <AgentTimeline steps={agentSteps} />}

      {/* AI Usage (admin only) */}
      {isAdmin && <UsageStats usage={usage} />}

      {/* No-PR decision (agent finished without opening a PR) */}
      {task?.noPrReviewStatus && (
        <NoPrDecisionCard
          taskId={task.id}
          category={task.noPrCategory}
          reason={task.noPrReason}
          reviewStatus={task.noPrReviewStatus}
          reviewedAt={task.noPrReviewedAt}
          onReviewed={handleNoPrReviewed}
        />
      )}

      {/* Pull Requests */}
      <Card>
        <div className="flex items-center gap-3 mb-4">
          <GitPullRequest className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Pull Requests</h3>
        </div>
        {pullRequests.length === 0 ? (
          <p className="text-gray-600 dark:text-gray-400">
            {hasNoPrDecision ? 'No pull request was opened for this ticket' : 'No pull requests yet'}
          </p>
        ) : (
          <div className="space-y-3">
            {pullRequests.map((pr) => (
              <div
                key={pr.id}
                className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:shadow-sm transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <Link
                      to={pr.prUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-2"
                    >
                      #{pr.prNumber} {pr.prTitle || 'No title'}
                      <ExternalLink className="w-3 h-3" />
                    </Link>
                    {pr.branchName && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Branch: {pr.branchName}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Created {new Date(pr.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <StatusBadge status={pr.status} size="sm" />
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
